import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';

const AI_STATUS_CONFIG = {
  Complete: { color: 'text-green-700 bg-green-50 border-green-200', icon: 'CheckCircle2', iconColor: '#16a34a' },
  Incomplete: { color: 'text-red-700 bg-red-50 border-red-200', icon: 'AlertCircle', iconColor: '#dc2626' },
};

const APPROVAL_STATUS_CONFIG = {
  Complete: { color: 'text-green-700 bg-green-50 border-green-200', icon: 'CheckCircle2', iconColor: '#16a34a' },
  Incomplete: { color: 'text-red-700 bg-red-50 border-red-200', icon: 'AlertCircle', iconColor: '#dc2626' },
  Pending: { color: 'text-amber-700 bg-amber-50 border-amber-200', icon: 'Clock', iconColor: '#d97706' },
};

const PAGE_SIZE = 10;

const mapAiStatus = (addressComplete) => addressComplete ? 'Complete' : 'Incomplete';

const mapApprovalStatus = (approvalStatus) => {
  if (approvalStatus === 'APPROVED') return 'Complete';
  if (approvalStatus === 'REJECTED') return 'Incomplete';
  return 'Pending';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatCurrency = (amount) => {
  if (amount == null) return '₹0';
  return `₹${Number(amount).toLocaleString('en-IN')}`;
};

const StatusBadge = ({ status, config }) => {
  const cfg = config?.[status] || config?.['Incomplete'];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${cfg?.color}`}>
      <Icon name={cfg?.icon} size={11} color={cfg?.iconColor} />
      {status}
    </span>
  );
};

const APPROVAL_OPTIONS = [
  { key: 'PENDING', label: 'Pending', icon: 'Clock', badge: 'text-amber-700 bg-amber-50 border-amber-200', iconColor: '#d97706' },
  { key: 'APPROVED', label: 'Complete', icon: 'CheckCircle2', badge: 'text-green-700 bg-green-50 border-green-200', iconColor: '#16a34a' },
  { key: 'REJECTED', label: 'Incomplete', icon: 'AlertCircle', badge: 'text-red-700 bg-red-50 border-red-200', iconColor: '#dc2626' },
];

const ApprovalCell = ({ orderId, current, onSetStatus, loading, dropUp }) => {
  const [open, setOpen] = useState(false);
  const currentOpt = APPROVAL_OPTIONS.find((o) => o.key === current) || APPROVAL_OPTIONS[0];

  const handleSelect = (key) => {
    setOpen(false);
    if (key !== current) onSetStatus(orderId, key);
  };

  return (
    <div className="relative inline-block">
      <div className="flex items-center gap-1.5">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${currentOpt.badge}`}>
          <Icon name={currentOpt.icon} size={11} color={currentOpt.iconColor} />
          {currentOpt.label}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
          disabled={loading}
          className="p-1 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
          title="Change status"
        >
          <Icon name="Pencil" size={12} />
        </button>
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div className={`absolute right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 w-40 py-1 ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
            {APPROVAL_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={(e) => { e.stopPropagation(); handleSelect(opt.key); }}
                className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors ${
                  current === opt.key
                    ? 'bg-blue-50 text-blue-700 font-semibold'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon name={opt.icon} size={12} color={opt.iconColor} />
                {opt.label}
                {current === opt.key && <Icon name="Check" size={12} color="#2563eb" className="ml-auto" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const SkeletonRow = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 8 })?.map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-4 bg-gray-100 rounded w-full" />
      </td>
    ))}
  </tr>
);

// ─── Address Detail Drawer ────────────────────────────────────────────────────
const AddressDrawer = ({ orderId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    api.get(`/api/v1/address-confirmation/orders/${orderId}`)
      .then((res) => {
        if (res.data?.success) setDetail(res.data.data);
      })
      .catch((err) => console.error('Failed to fetch order detail:', err))
      .finally(() => setLoading(false));
  }, [orderId]);

  if (!orderId) return null;

  const aiStatus = detail ? mapAiStatus(detail.addressComplete) : 'Incomplete';
  const approvalDisplay = detail ? mapApprovalStatus(detail.approvalStatus) : 'Pending';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity duration-300"
        onClick={onClose}
      />
      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col animate-slide-in-right">
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50/60">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Icon name="MapPin" size={16} color="#2563eb" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">{orderId}</p>
              {detail && <p className="text-xs text-gray-500">{detail.customerName}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Icon name="X" size={16} color="#6b7280" />
          </button>
        </div>

        {/* Drawer Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : detail ? (
            <>
              {/* Order Info */}
              <div className="bg-blue-50/60 rounded-xl border border-blue-100 p-4 space-y-3">
                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Order Details</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Order ID</p>
                    <p className="text-sm font-semibold text-blue-600">{detail.orderId}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Order Date</p>
                    <p className="text-sm font-medium text-gray-800">{formatDate(detail.orderDate)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Customer Name</p>
                    <p className="text-sm font-medium text-gray-800">{detail.customerName || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Mobile Number</p>
                    <p className="text-sm font-medium text-gray-800">{detail.phone || '—'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-400 mb-0.5">Order Value</p>
                    <p className="text-sm font-bold text-gray-900">{formatCurrency(detail.totalAmount)}</p>
                  </div>
                </div>
              </div>

              {/* Addresses */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="MapPin" size={14} color="#6b7280" />
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Delivery Address</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Input Address</p>
                  <p className="text-sm text-gray-800 leading-relaxed">{detail.inputAddress || '—'}</p>
                </div>
                {detail.formattedAddress && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Formatted Address</p>
                    <p className="text-sm text-gray-800 leading-relaxed">{detail.formattedAddress}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {detail.locality && (
                    <div>
                      <p className="text-xs text-gray-400">Locality</p>
                      <p className="text-sm text-gray-700">{detail.locality}</p>
                    </div>
                  )}
                  {detail.administrativeArea && (
                    <div>
                      <p className="text-xs text-gray-400">State</p>
                      <p className="text-sm text-gray-700">{detail.administrativeArea}</p>
                    </div>
                  )}
                  {detail.postalCode && (
                    <div>
                      <p className="text-xs text-gray-400">Postal Code</p>
                      <p className="text-sm text-gray-700">{detail.postalCode}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Address Verification Status */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Address Verification</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                      <Icon name="Bot" size={12} color="#7c3aed" />
                    </div>
                    <span className="text-sm text-gray-600">AI Address Status</span>
                  </div>
                  <StatusBadge status={aiStatus} config={AI_STATUS_CONFIG} />
                </div>
                <div className="h-px bg-gray-100" />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                      <Icon name="UserCheck" size={12} color="#2563eb" />
                    </div>
                    <span className="text-sm text-gray-600">Manual Approval</span>
                  </div>
                  <StatusBadge status={approvalDisplay} config={APPROVAL_STATUS_CONFIG} />
                </div>
              </div>

              {/* Validation Details */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Validation Details</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-gray-400">Input Granularity</p>
                    <p className="text-gray-700 font-medium">{detail.inputGranularity || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Validation Granularity</p>
                    <p className="text-gray-700 font-medium">{detail.validationGranularity || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Geocode Granularity</p>
                    <p className="text-gray-700 font-medium">{detail.geocodeGranularity || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Inferred Components</p>
                    <p className="text-gray-700 font-medium">{detail.hasInferredComponents ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Unconfirmed Components</p>
                    <p className="text-gray-700 font-medium">{detail.hasUnconfirmedComponents ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Replaced Components</p>
                    <p className="text-gray-700 font-medium">{detail.hasReplacedComponents ? 'Yes' : 'No'}</p>
                  </div>
                </div>
                {detail.missingComponentTypes && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Missing Components</p>
                    <p className="text-xs text-red-600 font-medium">{detail.missingComponentTypes}</p>
                  </div>
                )}
                {detail.unconfirmedComponentTypes && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Unconfirmed Components</p>
                    <p className="text-xs text-amber-600 font-medium">{detail.unconfirmedComponentTypes}</p>
                  </div>
                )}
              </div>

              {/* Metadata */}
              {(detail.latitude || detail.business != null || detail.residential != null) && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Metadata</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {detail.latitude && (
                      <div>
                        <p className="text-gray-400">Coordinates</p>
                        <p className="text-gray-700 font-medium">{detail.latitude}, {detail.longitude}</p>
                      </div>
                    )}
                    {detail.business != null && (
                      <div>
                        <p className="text-gray-400">Business</p>
                        <p className="text-gray-700 font-medium">{detail.business ? 'Yes' : 'No'}</p>
                      </div>
                    )}
                    {detail.residential != null && (
                      <div>
                        <p className="text-gray-400">Residential</p>
                        <p className="text-gray-700 font-medium">{detail.residential ? 'Yes' : 'No'}</p>
                      </div>
                    )}
                    {detail.poBox != null && (
                      <div>
                        <p className="text-gray-400">PO Box</p>
                        <p className="text-gray-700 font-medium">{detail.poBox ? 'Yes' : 'No'}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* AI Analysis Note */}
              <div className={`rounded-xl border p-4 ${aiStatus === 'Complete' ? 'bg-green-50 border-green-100' : 'bg-amber-50 border-amber-100'}`}>
                <div className="flex items-start gap-2.5">
                  <Icon
                    name={aiStatus === 'Complete' ? 'CheckCircle2' : 'AlertTriangle'}
                    size={16}
                    color={aiStatus === 'Complete' ? '#16a34a' : '#d97706'}
                  />
                  <div>
                    <p className={`text-xs font-semibold mb-1 ${aiStatus === 'Complete' ? 'text-green-700' : 'text-amber-700'}`}>
                      AI Analysis
                    </p>
                    <p className={`text-xs leading-relaxed ${aiStatus === 'Complete' ? 'text-green-600' : 'text-amber-600'}`}>
                      {aiStatus === 'Complete'
                        ? 'Address contains all required fields: house/flat number, street, city, state, and PIN code. Delivery is likely to succeed.'
                        : 'Address appears incomplete. Missing key details such as house number, PIN code, or landmark. Manual review recommended.'}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500 text-center py-8">Failed to load order details.</p>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/60">
          <p className="text-xs text-gray-400 text-center">Click outside or press × to close</p>
        </div>
      </div>
    </>
  );
};

// ─── Main Table ───────────────────────────────────────────────────────────────
const ACTable = ({ from, to, filters, refreshKey, onResetFilters }) => {
  const [rows, setRows] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  useEffect(() => {
    if (!from || !to) return;
    setLoading(true);
    const params = { from, to, page, size: PAGE_SIZE };
    if (filters?.addressStatus && filters.addressStatus !== 'all') params.status = filters.addressStatus;
    if (filters?.search) params.search = filters.search;

    api.get('/api/v1/address-confirmation/orders', { params })
      .then((res) => {
        if (res.data?.success) {
          const d = res.data.data;
          setRows(d.rows || []);
          setTotalOrders(d.totalOrders || 0);
          setTotalPages(d.totalPages || 0);
        }
      })
      .catch((err) => console.error('Failed to fetch AC orders:', err))
      .finally(() => setLoading(false));
  }, [from, to, page, filters?.addressStatus, filters?.search, refreshKey]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [from, to, filters?.addressStatus, filters?.search]);

  const handleApproval = async (orderId, approvalStatus) => {
    try {
      setApproving(orderId);
      await api.put(`/api/v1/address-confirmation/orders/${orderId}/approval`, { approvalStatus });
      // Update the row locally
      setRows((prev) => prev.map((r) =>
        r.orderId === orderId ? { ...r, approvalStatus } : r
      ));
    } catch (err) {
      console.error('Approval update failed:', err);
    } finally {
      setApproving(null);
    }
  };

  const thClass = "px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap bg-gray-50";

  if (!loading && rows.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center">
          <Icon name="MapPinOff" size={32} color="#d1d5db" />
        </div>
        <div className="text-center">
          <p className="text-base font-semibold text-gray-700">No order found</p>
          <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or search terms</p>
        </div>
        <button
          onClick={onResetFilters}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
        >
          Reset Filters
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className={thClass}>Order Date</th>
                <th className={thClass}>Order ID</th>
                <th className={thClass}>Customer Name</th>
                <th className={thClass}>Mobile Number</th>
                <th className={thClass}>Order Value</th>
                <th className={thClass}>Address</th>
                <th className={thClass}>AI Address Status</th>
                <th className={thClass}>Manual Approval</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: PAGE_SIZE })?.map((_, i) => <SkeletonRow key={i} />)
                : rows.map((row, idx) => {
                    const aiStatus = mapAiStatus(row.addressComplete);
                    return (
                      <tr
                        key={row.orderId}
                        onClick={() => setSelectedOrderId(row.orderId)}
                        className={`border-b border-gray-50 transition-colors duration-100 hover:bg-blue-50/40 cursor-pointer ${
                          idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                        }`}
                      >
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap text-xs">{formatDate(row.orderDate)}</td>
                        <td className="px-4 py-3 font-semibold text-blue-600 whitespace-nowrap text-xs">{row.orderId}</td>
                        <td className="px-4 py-3 text-gray-800 font-medium whitespace-nowrap">{row.customerName || '—'}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap text-xs">{row.phone || '—'}</td>
                        <td className="px-4 py-3 text-gray-800 font-semibold whitespace-nowrap">{formatCurrency(row.totalAmount)}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs max-w-[180px]">
                          <span className="truncate block" title={row.formattedAddress || row.address}>{row.formattedAddress || row.address || '—'}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <StatusBadge status={aiStatus} config={AI_STATUS_CONFIG} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <ApprovalCell
                            orderId={row.orderId}
                            current={row.approvalStatus || 'PENDING'}
                            onSetStatus={handleApproval}
                            loading={approving === row.orderId}
                            dropUp={idx >= rows.length - 2}
                          />
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <p className="text-xs text-gray-500">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalOrders)} of {totalOrders} orders
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-white hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="ChevronLeft" size={14} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) })?.map((_, i) => {
                let pg;
                if (totalPages <= 5) {
                  pg = i;
                } else if (page < 3) {
                  pg = i;
                } else if (page > totalPages - 4) {
                  pg = totalPages - 5 + i;
                } else {
                  pg = page - 2 + i;
                }
                return (
                  <button
                    key={pg}
                    onClick={() => setPage(pg)}
                    className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${
                      page === pg ? 'bg-blue-600 text-white' : 'border border-gray-200 text-gray-600 hover:bg-white hover:border-blue-300'
                    }`}
                  >
                    {pg + 1}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page === totalPages - 1}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-white hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="ChevronRight" size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Address Detail Drawer */}
      {selectedOrderId && (
        <AddressDrawer
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
        />
      )}
    </>
  );
};

export default ACTable;
